'use client';

import { useEffect, useMemo, useState } from 'react';
import type { Brand, Category, Device, Product } from '@/models';
import {
  getDeviceBySlug,
  listBrands,
  listCategories,
  listCompatibleProducts,
} from '@/services/catalog.service';
import { listViewerWholesalePrices } from '@/services/pricing.service';

export interface DeviceDetailApi {
  device: Device | null;
  brand: Brand | null;
  /** Only categories present in the loaded compatible assortment, in catalog order. */
  categories: Category[];
  /** Full compatible count (unfiltered). */
  compatibleCount: number;
  /** Compatibles narrowed by the selected category chip (in-memory). */
  visibleProducts: Product[];
  selectedCategory: string | null;
  setSelectedCategory: (slug: string | null) => void;
  loading: boolean;
  notFound: boolean;
}

/**
 * Client-side state seam for the mobile device detail view.
 * Resolves the device by slug once, loads its compatible assortment
 * (wholesale-aware via the shared pricing seam), then filters the
 * loaded grid in memory by category chip. Presentational views
 * consume only this API.
 */
export function useDeviceDetail(slug: string): DeviceDetailApi {
  const [device, setDevice] = useState<Device | null>(null);
  const [brand, setBrand] = useState<Brand | null>(null);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setSelectedCategory(null);

    getDeviceBySlug(slug)
      .then((loadedDevice) => {
        if (cancelled) {
          return;
        }
        if (!loadedDevice) {
          setDevice(null);
          setBrand(null);
          setAllCategories([]);
          setAllProducts([]);
          setNotFound(true);
          setLoading(false);
          return;
        }
        setDevice(loadedDevice);
        return Promise.all([
          listBrands(),
          listCategories(),
          listCompatibleProducts(loadedDevice.id),
          listViewerWholesalePrices(),
        ]).then(([loadedBrands, loadedCategories, compatibles, wholesalePrices]) => {
          if (cancelled) {
            return;
          }
          setBrand(loadedBrands.find((item) => item.id === loadedDevice.brandId) ?? null);
          setAllCategories(loadedCategories);
          setAllProducts(
            compatibles.map((product) =>
              wholesalePrices[product.id] != null
                ? { ...product, wholesalePrice: wholesalePrices[product.id] }
                : product
            )
          );
          setLoading(false);
        });
      })
      .catch(() => {
        if (!cancelled) {
          setDevice(null);
          setBrand(null);
          setAllCategories([]);
          setAllProducts([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const categories = useMemo(() => {
    const present = new Set(allProducts.map((product) => product.categorySlug));
    return allCategories.filter((category) => present.has(category.slug));
  }, [allCategories, allProducts]);

  const visibleProducts = useMemo(() => {
    if (!selectedCategory) {
      return allProducts;
    }
    return allProducts.filter((product) => product.categorySlug === selectedCategory);
  }, [allProducts, selectedCategory]);

  return {
    device,
    brand,
    categories,
    compatibleCount: allProducts.length,
    visibleProducts,
    selectedCategory,
    setSelectedCategory,
    loading,
    notFound,
  };
}
